import { format } from 'date-fns';
import { soldOnComplimentaryOption } from '@/lib/membership/complimentary-option';
import { dateStringToDate } from '@/lib/date-values';
import type { Membership, MembershipSource, MembershipStatus } from '@/types/membership';

export const MEMBERSHIP_PAGE_SIZE = 20;

export type MembershipSourceFilter = MembershipSource | 'all';
export type MembershipQueueFilter =
  | 'all'
  | 'RENEWAL_DUE'
  | 'UNDERUTILIZED'
  | 'RECOVERY'
  | 'SAVE_FLOW';

export interface MembershipListFilters {
  q: string;
  status: MembershipStatus | 'all';
  source: MembershipSourceFilter;
  queue: MembershipQueueFilter;
  page: number;
  sort: number;
}

export const DEFAULT_MEMBERSHIP_LIST_FILTERS: MembershipListFilters = {
  q: '',
  status: 'all',
  source: 'all',
  queue: 'all',
  page: 1,
  sort: 0,
};

export const MEMBERSHIP_STATUS_FILTERS: Array<{
  value: MembershipStatus | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

export const MEMBERSHIP_STATUS_DOT_COLORS: Record<string, string> = {
  ACTIVE: 'bg-emerald-500',
  PENDING: 'bg-amber-500',
  PAST_DUE: 'bg-amber-500',
  SUSPENDED: 'bg-orange-500',
  CANCELLED: 'bg-red-500',
  EXPIRED: 'bg-zinc-400',
};

export const MEMBERSHIP_SOURCE_FILTERS: Array<{
  value: MembershipSourceFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'ESTIMATE', label: 'Estimate' },
  { value: 'JOB', label: 'Job' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'AI_GENERATED', label: 'AI Generated' },
];

export const MEMBERSHIP_QUEUE_FILTERS: Array<{
  value: MembershipQueueFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'RENEWAL_DUE', label: 'Renewal Due' },
  { value: 'UNDERUTILIZED', label: 'Underutilized' },
  { value: 'RECOVERY', label: 'Recovery' },
  { value: 'SAVE_FLOW', label: 'Save Flow' },
];

export function formatMembershipPlanName(membership: Membership) {
  return (membership.planSnapshot?.name as string | undefined) || 'Unknown Plan';
}

// Date-only (@db.Date) fields — nextBillingDate, currentPeriodEnd, etc. Rendered
// tz-less from the calendar date so the day never shifts across timezones.
export function formatMembershipDate(value: string | null | undefined) {
  const date = dateStringToDate(value);
  return date ? format(date, 'MMM d, yyyy') : '—';
}

export function formatMembershipShortDate(value: string | null | undefined) {
  const date = dateStringToDate(value);
  return date ? format(date, 'MMM d') : '—';
}

export function formatMembershipMoney(amount: number | null | undefined, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount ?? 0);
}

export function formatMembershipBilling(membership: Membership) {
  // Given on a free option with nothing left to bill — not a "$0.00/yr" charge.
  if (soldOnComplimentaryOption(membership.planSnapshot) && !(membership.billingAmount > 0)) {
    return 'Free';
  }
  const amount = formatMembershipMoney(membership.billingAmount, membership.billingCurrency);
  const unit = membership.billingIntervalUnit;
  const value = membership.billingIntervalValue;

  if (unit === 'MONTHS') return `${amount}${value > 1 ? `/${value}mo` : '/mo'}`;
  if (unit === 'YEARS') return `${amount}${value > 1 ? `/${value}yr` : '/yr'}`;
  return `${amount}/${unit.toLowerCase()}`;
}

export function formatMembershipSourceLabel(source: MembershipSource) {
  return MEMBERSHIP_SOURCE_FILTERS.find((filter) => filter.value === source)?.label ?? source;
}
